<script lang="ts" setup>
import { Plus, Trash01, Pencil02, X } from "utruckin-icons";
import CommonButton from "@/components/common/CommonButton.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import { useProviderStore } from "@/features/provider/provider.store";
import { onMounted, ref } from "vue";
import { IProvider } from "@/features/provider/provider.types";
import CommonModal from "@/components/common/CommonModal.vue";
import CommonInput from "@/components/common/CommonInput.vue";
import { useNotification } from "@/utils/notification";
import router from "@/router";

const providerStore = useProviderStore();

const { pushNotification } = useNotification();

const loading = ref(false);
const providers = ref<IProvider[]>([]);

const modalItem = ref<any>();
const modalLoading = ref<boolean>(false);
const isModalVisible = ref<boolean>(false);
const modalType = ref<"create" | "edit">("create");

const getList = async () => {
  await providerStore.getList(loading, (res: any) => {
    if (res) {
      providers.value = res;
    }
  });
};

const closeModal = () => {
  modalItem.value = {};
  isModalVisible.value = false;
  modalLoading.value = false;
};

const saveModal = async () => {
  if (modalType.value == "create") {
    if (
      !modalItem.value.name ||
      !modalItem.value.app_name ||
      !modalItem.value.api_token
    )
      return;

    const organizationId: string = localStorage.getItem(
      "organizationId"
    ) as string;
    if (!organizationId) router.push("/organization");

    await providerStore.createProvider(
      {
        organization_id: organizationId,
        name: modalItem.value.name,
        app_name: modalItem.value.app_name,
        api_token: modalItem.value.api_token,
        is_active: true,
      },
      modalLoading,
      async (data: any) => {
        if (data) {
          pushNotification("Successfully created", "success");
          closeModal();
          await getList();
        }
      }
    );
  } else {
    if (!modalItem.value.name || !modalItem.value.app_name) return;

    await providerStore.updateProvider(
      {
        name: modalItem.value.name,
        app_name: modalItem.value.app_name,
        is_active: true,
      },
      modalItem.value.id,
      modalLoading,
      async (data: any) => {
        if (data) {
          pushNotification("Successfully saved", "success");
          closeModal();
          await getList();
        }
      }
    );
  }
};

const openModal = (type: "create" | "edit", item?: any) => {
  if (type == "create") {
    modalItem.value = {
      name: "",
      app_name: "",
      api_token: "",
      is_active: true,
    };
    modalType.value = "create";
  } else {
    modalItem.value = item;
    modalType.value = "edit";
  }
  modalLoading.value = false;
  isModalVisible.value = true;
};

const turnOff = async (item: any) => {
  const payload = {
    name: item.name,
    app_name: item.app_name,
    is_active: false,
  };
  await providerStore.updateProvider(
    payload,
    item.id,
    loading,
    async (data: any) => {
      if (data) {
        pushNotification("Successfully made inactive", "success");
        await getList();
      }
    }
  );
};

onMounted(async () => {
  await getList();
});
</script>

<template>
  <div class="v-provider">
    <div class="header">
      <div class="header__stats">
        <CommonButton
          type="primary"
          label="Add provider"
          prepend
          :prependIcon="Plus"
          @click="openModal('create')"
        />
      </div>
    </div>

    <CommonTable
      :headers="providerStore.providerHeaders"
      :loading="loading"
      :items="providers"
    >
      <template #id="{ index }">
        <span>{{ index + 1 }}</span>
      </template>

      <template #token> ******************</template>

      <template #status="{ item }">
        <div class="status" :class="item.is_active ? '' : 'inactive'">
          {{ item.is_active ? "active" : "inactive" }}
        </div>
      </template>

      <template #actions="{ item }">
        <div class="action">
          <div
            class="action__item"
            v-tooltip="{
              content: 'Edit',
              html: true,
            }"
            @click="openModal('edit', item)"
          >
            <Pencil02 :size="18" />
          </div>
          <div
            class="action__item"
            v-tooltip="{
              content: 'Make Inactive',
              html: true,
            }"
            @click="turnOff(item)"
          >
            <Trash01 :size="18" />
          </div>
        </div>
      </template>
    </CommonTable>

    <CommonModal
      v-model="isModalVisible"
      :title="modalType == 'create' ? 'Add a provider' : 'Edit a provider'"
      @close="closeModal"
    >
      <template v-if="modalItem">
        <div class="info">
          <CommonInput v-model="modalItem.name" label="Name" />
        </div>
        <div class="info">
          <CommonInput v-model="modalItem.app_name" label="App name" />
        </div>
        <div class="info" v-if="modalType == 'create'">
          <CommonInput v-model="modalItem.api_token" label="Token" />
        </div>
      </template>

      <template #footer>
        <div class="description__footer">
          <CommonButton type="outline" label="Cancel" @clicked="closeModal" />
          <CommonButton type="primary" label="Save" @clicked="saveModal" />
        </div>
      </template>
    </CommonModal>
  </div>
</template>

<style lang="scss" scoped>
.v-provider {
  display: flex;
  flex-direction: column;
  padding: var(--base-space-24);
  height: calc(100vh - 80px);
  background: var(--color-system-background);
  color: var(--color-label);

  .header {
    display: flex;
    align-items: center;
    justify-content: end;
  }

  .status {
    border: 1px solid var(--color-border);
    border-radius: 12px;
    background: var(--color-secondary-system-background);
    color: var(--color-system-green);
    display: inline-block;
    padding: 2px 8px;

    &.inactive {
      background: var(--color-red-lighter);
      color: var(--color-system-red);
    }
  }

  .action {
    display: flex;
    align-items: center;
    gap: 8px;

    &__item {
      width: 32px;
      height: 32px;
      border: 1px solid var(--color-border);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--color-secondary-system-background);
    }
  }
}
.info {
  margin-bottom: 16px;
}

.description__footer {
  width: 100%;
  gap: var(--base-space-8);
  display: flex;
  align-items: center;
  justify-content: end;
}
</style>
